import React from 'react';

interface WidgetSkeletonProps {
    rows?: number;
    className?: string;
    withHeader?: boolean;
    compact?: boolean;
}

function SkeletonBar({ className = '' }: { className?: string }) {
    return (
        <div className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
    );
}

function WidgetSkeleton(props: WidgetSkeletonProps) {
    const { rows = 4, className = '', withHeader = true, compact = false } = props;
    const rowCount = Math.max(1, rows);

    return (
        <div
            className={`rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900 ${className}`}
            data-testid="widget-skeleton"
            aria-busy="true"
            aria-live="polite"
        >
            {withHeader && (
                <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                    <SkeletonBar className="h-5 w-40" />
                </div>
            )}
            <div className={`space-y-3 ${compact ? 'p-3' : 'p-4'}`}>
                {Array.from({ length: rowCount }).map((_, index) => (
                    <div key={index} className="space-y-2">
                        <SkeletonBar className="h-4 w-full" />
                        <SkeletonBar className="h-4 w-3/4" />
                    </div>
                ))}
            </div>
        </div>
    );
}

export function BriefingSkeleton() {
    return (
        <div
            className="border-b bg-white px-4 py-4 dark:bg-gray-900 sm:px-6 lg:px-8"
            data-testid="briefing-skeleton"
            aria-busy="true"
            aria-live="polite"
        >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 space-y-2">
                    <SkeletonBar className="h-6 w-56" />
                    <SkeletonBar className="h-4 w-72" />
                    <SkeletonBar className="mt-2 h-2 max-w-md" />
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {Array.from({ length: 4 }).map((_, index) => (
                        <div
                            key={index}
                            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
                        >
                            <SkeletonBar className="h-3 w-16" />
                            <SkeletonBar className="mt-2 h-7 w-10" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default WidgetSkeleton;
